import React, { useEffect, useReducer, useState } from 'react';
import { MdOutlinePayments } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '../../toasts';
import BusyOverlay from '../../components/BusyOverlay/BusyOverlay';
import ContactService from '../../services/contact.service';
import { getError } from '../../utils';
import ContactInfo from '../Contacts/ContactInfo';
import InfoTabs from '../../components/InfoTabs/InfoTabs';
import DetailViewerHeader from '../../components/ObjectIconDisplay/DetailViewerHeader';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import HistoryModal from '../../components/HistoryModal';
import SingleObjectDocumentManager from '../../components/SingleObjectDocumentManager/SingleObjectDocumentManager';
import { useLabels } from '../../hooks/useLabels';

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return { ...state, contact: action.payload, loading: false };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	default:
		return state;
	}
};

export default function ContactDetail() {
	const { t } = useLabels();
	const params = useParams();
	const navigate = useNavigate();

	const { id } = params;
	const [{ contact, loading }, dispatch] = useReducer(reducer, {
		contact: {},
		loading: true,
		error: '',
	});

	const [showHistoryLog, setShowHistoryLog] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);

	const handleShowHistoryLog = () => {
		setShowHistoryLog(true);
	};

	const handleCloseHistoryLog = () => {
		setShowHistoryLog(false);
	};

	useEffect(() => {
		const fetchData = async () => {
			dispatch({ type: 'FETCH_REQUEST' });
			try {
				const result = await ContactService.getDetails(id);
				dispatch({ type: 'FETCH_SUCCESS', payload: result.data });
			} catch (error) {
				dispatch({ type: 'FETCH_FAIL', payload: getError(error) });
				console.error(getError(error));
				toast.error('No se pudo obtener el contacto');
			}
		};
		fetchData();
	}, [id]);

	async function editContactHandler() {
		navigate(`/AdminScreen/contacts/editContact/${id}`);
	}

	function deleteContactHandler() {
		setShowDeleteModal(true);
	}

	async function handleConfirmDelete() {
		try {
			await ContactService.delete(id);
			toast.success('Contacto Eliminado');
			navigate('/AdminScreen/contacts');
		} catch (err) {
			toast.error(getError(err));
		}
	}

	function handleCloseDeleteModal() {
		setShowDeleteModal(false);
	}

	const buttons = [
		{
			label: 'Editar',
			onClick: editContactHandler,
			type: 'edit',
		},
		{
			label: 'Eliminar',
			onClick: deleteContactHandler,
			type: 'delete',
		},
		{
			label: 'Atras',
			onClick: () => navigate(-1),
		}
	];

	return (
		<>
			<BusyOverlay active={loading} />
			<div className='container admin-con align-items-center'>
				<HistoryModal model="contact" show={showHistoryLog} handleClose={handleCloseHistoryLog} id={id} isHistory={true}/>
				<DetailViewerHeader 
					icon={<MdOutlinePayments />} 
					title={`Contacto: ${contact.name || ''}`}
					openHistoryLogHandler={handleShowHistoryLog}
					buttons={buttons} 
				/>
				<div className="info-container">
					<InfoTabs 
						tabbedComponents={[
							{ eventKey: 'info', title: 'Detalles', component: <ContactInfo /> },
							{ eventKey: 'documents', title: 'Documentos', component: (
								<div className='p-3'>
									<SingleObjectDocumentManager 
										objectId={id} 
										objectType="contact" 
										documentType="contact" 
									/>
								</div>
							) }
						]}
						defaultActiveKey="info"
						justify={true}
						className="mb-3"
					/>
				</div>
			</div>
			<ConfirmationModal
				show={showDeleteModal}
				handleClose={handleCloseDeleteModal}
				handleConfirm={handleConfirmDelete}
				title={t('title.deleteContact')}
				description={t('msg.confirmDeleteContact')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
		</>
	);
}
